import { useMemo } from "react";
import { useApi, apiResolver } from "@/api/useApi";
import {
  buildBrowserSkillParameters,
  buildSkillSlug,
  Skill,
  SkillParameters,
} from "./Skill.model";

export type CreateSkillPayload = {
  name: string;
  description: string;
  curl: string;
  slug: string;
  parameters?: SkillParameters;
  type?: string;
  responseFields?: string[];
};

export type UpdateSkillPayload = {
  id: string;
  name?: string;
  description?: string;
  curl?: string;
  parameters?: SkillParameters;
  type?: string;
  requiresApproval?: boolean;
  responseFields?: string[] | null;
};

export type SampleSkillResponse = unknown;

export type CreateBrowserSkillPayload = {
  name: string;
  description: string;
  startUrl: string;
  slug?: string;
  profileName?: string | null;
};

export type UpdateBrowserSkillPayload = CreateBrowserSkillPayload & {
  id: string;
};

export type CreateBrowserSessionResponse = {
  id: string;
  liveUrl?: string;
  sessionUrl?: string;
  token?: string;
  wsEndpoint?: string;
  launchState?: {
    profile?: {
      id?: string;
    };
  };
};

export type StopBrowserSessionResponse = {
  success: boolean;
};

export type BrowserProfileIdResponse = {
  profileId?: string | null;
  profileName: string;
};

export type DeleteBrowserProfileResponse = {
  success: boolean;
};

type SkillApi = {
  getSkills: () => Promise<Skill[]>;
  getSkill: (skillId: string) => Promise<Skill>;
  createSkill: (payload: CreateSkillPayload) => Promise<Skill>;
  updateSkill: (payload: UpdateSkillPayload) => Promise<Skill>;
  createBrowserSkill: (payload: CreateBrowserSkillPayload) => Promise<Skill>;
  updateBrowserSkill: (payload: UpdateBrowserSkillPayload) => Promise<Skill>;
  createBrowserSession: (startUrl: string, profileName: string) => Promise<CreateBrowserSessionResponse>;
  stopBrowserSession: (sessionId: string, profileName: string) => Promise<StopBrowserSessionResponse>;
  getBrowserProfileId: (profileName: string) => Promise<BrowserProfileIdResponse>;
  deleteBrowserProfile: (profileId: string) => Promise<DeleteBrowserProfileResponse>;
  deleteSkill: (skillId: string) => Promise<Skill>;
  sampleSkill: (skillId: string, parameters?: Record<string, unknown>) => Promise<SampleSkillResponse>;
};

export function useSkillApi(): SkillApi {
  const { api } = useApi();

  const buildBrowserSkillPayload = useMemo(
    () =>
      ({
        name,
        description,
        startUrl,
        slug,
        profileName,
      }: CreateBrowserSkillPayload) => ({
        name,
        description,
        slug: slug ?? buildSkillSlug(name),
        curl: "curl is not relevant for built in skills",
        parameters: buildBrowserSkillParameters(
          startUrl,
          profileName
        ),
      }),
    []
  );

  return useMemo(
    () => ({
      getSkills: async () => apiResolver<Skill[]>(api.get("/platform/skills")),
      getSkill: async (skillId: string) =>
        apiResolver<Skill>(api.get(`/platform/skills/${skillId}`)),
      createSkill: async (payload: CreateSkillPayload) =>
        apiResolver<Skill>(api.post("/platform/skills", payload)),
      updateSkill: async ({ id, ...payload }: UpdateSkillPayload) =>
        apiResolver<Skill>(api.put(`/platform/skills/${id}`, payload)),
      createBrowserSkill: async (payload: CreateBrowserSkillPayload) =>
        apiResolver<Skill>(api.post("/platform/skills", buildBrowserSkillPayload(payload))),
      updateBrowserSkill: async ({ id, ...payload }: UpdateBrowserSkillPayload) =>
        apiResolver<Skill>(
            api.put(`/platform/skills/${id}`, buildBrowserSkillPayload(payload))
        ),
      createBrowserSession: async (startUrl: string, profileName: string) => {
        const response = await apiResolver<{ data: CreateBrowserSessionResponse }>(
            api.post("/platform/browsing/sessions", { startUrl, profileName })
        );
        return response.data;
      },
      stopBrowserSession: async (sessionId: string, profileName: string) => {
        const response = await apiResolver<{ data: StopBrowserSessionResponse }>(
            api.post("/platform/browsing/session/stop", { sessionId, profileName })
        );
        return response.data;
      },
      getBrowserProfileId: async (profileName: string) => {
        const response = await apiResolver<{ data: BrowserProfileIdResponse }>(
            api.get("/platform/browsing/profile/lookup", { params: { profileName } })
        );
        return response.data;
      },
      deleteBrowserProfile: async (profileId: string) => {
        const response = await apiResolver<{ data: DeleteBrowserProfileResponse }>(
            api.delete(`/platform/browsing/profile/${profileId}`)
        );
        return response.data;
      },
      deleteSkill: async (skillId: string) =>
        apiResolver<Skill>(api.delete(`/platform/skills/${skillId}`)),
      sampleSkill: async (skillId: string, parameters?: Record<string, unknown>) =>
        apiResolver<SampleSkillResponse>(api.post(`/platform/skills/${skillId}/sample`, parameters ? { parameters } : undefined)),
    }),
    [api, buildBrowserSkillPayload]
  );
}
